import apiClient from './config';
import { handleApiError } from './utils';
import { 
  PrepareEximbayPaymentRequest, 
  PrepareEximbayPaymentResponse,
  VerifyEximbayPaymentRequest,
  VerifyEximbayPaymentResponse
} from '../types/payment';

/**
 * 엑심베이 결제 준비 API 호출
 * @param orderId 주문 ID
 * @param data 결제 준비 요청 데이터
 * @returns fgkey가 포함된 응답 데이터
 */
export const prepareEximbayPayment = async (
  orderId: string, 
  data: PrepareEximbayPaymentRequest
): Promise<PrepareEximbayPaymentResponse> => {
  try {
    const response = await apiClient.post<PrepareEximbayPaymentResponse>(
      `/api/orders/${orderId}/payment/eximbay/prepare`,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * 엑심베이 결제 검증 API 호출 (웹훅 기반이므로 실제로는 사용하지 않을 예정)
 * @param orderId 주문 ID
 * @param data 결제 검증 요청 데이터
 * @returns 주문 정보
 */
export const verifyEximbayPayment = async (
  orderId: string,
  data: VerifyEximbayPaymentRequest
): Promise<VerifyEximbayPaymentResponse> => {
  try {
    const response = await apiClient.post<VerifyEximbayPaymentResponse>(
      `/api/orders/${orderId}/payment/eximbay`,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * 엑심베이 결제 상태 조회 (백엔드 엑심베이 조회 API 활용)
 * @param orderId 주문 ID
 * @param currency 통화
 * @param amount 금액
 * @param lang 언어
 * @returns 엑심베이 결제 상태 정보
 */
export const checkEximbayPaymentStatus = async (
  orderId: string,
  currency: string,
  amount: number,
  lang: string
): Promise<{
  rescode: string;
  resmsg: string;
  isPaid: boolean;
  rawResponse?: any;
}> => {
  const maxRetries = 3;
  let lastError: any;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await apiClient.post(
        '/api/orders/payment/eximbay/retrieve',
        {
          keyField: 'order_id',
          orderId: orderId,
          currency: currency,
          amount: amount.toString(),
          lang: lang
        },
        { timeout: 3000 }
      );
      
      return {
        rescode: response.data.rescode || 'ERROR',
        resmsg: response.data.resmsg || 'Unknown error',
        isPaid: response.data.rescode === '0000',
        rawResponse: response.data.rawResponse
      };
    } catch (error) {
      lastError = error;
      console.log(`엑심베이 조회 시도 ${attempt}/${maxRetries} 실패:`, error);
      
      // 마지막 시도가 아니면 잠시 대기
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }
  
  // 모든 재시도 실패
  throw handleApiError(lastError);
};

/**
 * 주문 상태 조회 (웹훅으로 처리된 주문 확인용)
 * @param orderId 주문 ID
 * @returns 주문 정보
 */
export const getOrderStatus = async (orderId: string): Promise<VerifyEximbayPaymentResponse> => {
  try {
    const response = await apiClient.get<VerifyEximbayPaymentResponse>(
      `/api/orders/${orderId}`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};
